import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';

import { getRouteUrl } from '@utils/urls';
import { formatDate } from '@utils/date';

import { JOBS_JOB_OFFER_DETAILS } from '@common/routes';
import { ButtonLink } from '@common/components/Button';

import {
  ButtonContainer,
  Container,
  DividedSection,
  PlaceNameAddition,
  Section,
  SectionSecondaryValue,
  SectionTitle,
  SectionValue,
  Title,
  WorkloadPercentage,
} from './JobOfferInfo.styled';

const JobOfferInfo = ({
  apprenticeship,
  buttonText,
  className,
  dateOfEntry,
  fixedTermDate,
  highlights,
  id,
  placeAsTitle,
  placeOfWork,
  slug,
  title,
  workloadFrom,
  workloadTo,
  withLinkButton,
  placeNameAddition,
  ...props
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const heading = placeAsTitle ? placeOfWork : title;
  const today = new Date();
  const date = new Date(dateOfEntry);

  const isOutdated = differenceInCalendarDays(date, today) < 0;
  const displayDate = formatDate(date, language);
  const employmentPerioDate = fixedTermDate ? new Date(fixedTermDate) : null;
  const displayEmploymentPeriodDate = employmentPerioDate
    ? formatDate(employmentPerioDate, language)
    : null;

  return (
    <Container className={className} {...props}>
      {workloadFrom && workloadTo && (
        <WorkloadPercentage>{`${workloadFrom}% - ${workloadTo}%`}</WorkloadPercentage>
      )}

      {workloadFrom && !workloadTo && (
        <WorkloadPercentage>{`${workloadFrom}%`}</WorkloadPercentage>
      )}

      <Title>{heading}</Title>
      {placeAsTitle && placeNameAddition && (
        <PlaceNameAddition variant="body2">
          {placeNameAddition}
        </PlaceNameAddition>
      )}
      <div>
        {!apprenticeship && placeAsTitle && title && (
          <Section>
            <SectionTitle>
              {t('jobsOffersListItem.jobSectionLabel', 'Tätigkeit')}
            </SectionTitle>
            <SectionValue>{title}</SectionValue>
          </Section>
        )}
        {!placeAsTitle && !apprenticeship && (
          <Section>
            <SectionTitle>
              {t('jobsOffersListItem.placeSectionLabel', 'Arbeitsort')}
            </SectionTitle>
            <SectionValue>{placeOfWork}</SectionValue>
            {!placeAsTitle && placeNameAddition && (
              <PlaceNameAddition variant="body2" noSpacing>
                {placeNameAddition}
              </PlaceNameAddition>
            )}
          </Section>
        )}
        {!apprenticeship && !isOutdated && (
          <Section>
            <SectionTitle>
              {t('jobsOffersListItem.dateSectionLabel', 'Eintritt')}
            </SectionTitle>
            <SectionValue>
              {t(
                'jobsOffersListItem.dateSection.dateOfEntry',
                'Per {{date}} oder nach Vereinbarung',
                {
                  date: displayDate,
                },
              )}
            </SectionValue>
          </Section>
        )}
        {!apprenticeship && isOutdated && (
          <Section>
            <SectionTitle>
              {t('jobsOffersListItem.dateSectionLabel', 'Eintritt')}
            </SectionTitle>
            <SectionValue>
              {t(
                'jobsOffersListItem.dateSection.immediately',
                'Per sofort oder nach Vereinbarung',
              )}
            </SectionValue>
          </Section>
        )}
        {apprenticeship && (
          <Section>
            <SectionTitle>
              {t('jobsOffersListItem.apprenticeshipSectionLabel', 'Lehrbeginn')}
            </SectionTitle>
            <SectionValue>
              {t('jobsOffersListItem.apprenticeshipStartDate', 'Per {{date}}', {
                date: displayDate,
              })}
            </SectionValue>
          </Section>
        )}
        {!apprenticeship && fixedTermDate && (
          <Section>
            <SectionTitle>
              {t(
                'jobsOffersListItem.employmentPeriodSectionLabel',
                'Anstellung',
              )}
            </SectionTitle>
            <SectionValue>
              {t(
                'jobsOffersListItem.employmentSection.dateOfTerm',
                'Befristet bis {{date}}',
                {
                  date: displayEmploymentPeriodDate,
                },
              )}
            </SectionValue>
          </Section>
        )}
        {!apprenticeship && !fixedTermDate && (
          <Section>
            <SectionTitle>
              {t(
                'jobsOffersListItem.employmentPeriodSectionLabel',
                'Anstellung',
              )}
            </SectionTitle>
            <SectionValue>
              {t(
                'jobsOffersListItem.employmentSection.unknownDateOfTerm',
                'Unbefristet',
              )}
            </SectionValue>
          </Section>
        )}
        {highlights?.length > 0 && (
          <DividedSection>
            {highlights.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <SectionSecondaryValue key={index}>{item}</SectionSecondaryValue>
            ))}
          </DividedSection>
        )}
      </div>
      {withLinkButton && (
        <ButtonContainer>
          <Link
            legacyBehavior
            href={{
              pathname: JOBS_JOB_OFFER_DETAILS,
              query: {
                id,
                slug,
              },
            }}
            as={getRouteUrl(language, JOBS_JOB_OFFER_DETAILS, {
              id,
              slug,
            })}
            passHref
          >
            <ButtonLink>{buttonText}</ButtonLink>
          </Link>
        </ButtonContainer>
      )}
    </Container>
  );
};

JobOfferInfo.propTypes = {
  apprenticeship: PropTypes.bool,
  buttonText: PropTypes.string.isRequired,
  className: PropTypes.string,
  dateOfEntry: PropTypes.string.isRequired,
  fixedTermDate: PropTypes.string,
  highlights: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.number.isRequired,
  placeAsTitle: PropTypes.bool,
  placeOfWork: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  withLinkButton: PropTypes.bool,
  workloadFrom: PropTypes.number,
  workloadTo: PropTypes.number,
  placeNameAddition: PropTypes.string,
};

JobOfferInfo.defaultProps = {
  apprenticeship: false,
  className: undefined,
  fixedTermDate: undefined,
  highlights: undefined,
  placeAsTitle: false,
  withLinkButton: false,
  workloadFrom: undefined,
  workloadTo: undefined,
  placeNameAddition: '',
};

export default JobOfferInfo;
